import { StandardEvents } from '@shopify/events';

/*
 * ATW cart suggestions («Πριν το ταμείο»), drawer + /cart page.
 *
 * The content is fetched lazily from the atw-cart-contextual section. State is derived from the DOM
 * (is the title rendered?) instead of a sticky "loaded" flag: the theme re-renders the cart with the Section
 * Rendering API and morphs the (server-empty) element, which wipes the rail. The old flag stayed true after
 * that, so a drawer that was morphed while closed never loaded again when reopened.
 */
class ATWCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dirty = false;
    this.enableMouseDrag();
    const drawer = this.closest('theme-drawer');

    // Active = the shopper can actually see it (drawer open, or the /cart page).
    this.isActive = () => !drawer || drawer.hasAttribute('open');
    this.load = () => {
      if (!this.isConnected || this.pending || this.hasContent()) return;
      this.run();
    };

    // The cart changed and the theme wiped the rail (the server markup of this element is always empty).
    // Closed drawer: stay empty, the open event loads fresh suggestions. Open drawer / page: reload now.
    // A request already in flight started before the change, so its answer is stale and gets replaced.
    this.refreshObserver = new MutationObserver((records) => {
      if (!records.some((record) => record.removedNodes.length) || this.firstElementChild) return;
      this.dirty = true;
      if (this.isActive()) this.load();
    });
    this.refreshObserver.observe(this, { childList: true });

    // A cart change while the first request is still running (nothing to wipe yet, so no DOM mutation):
    // mark that request stale, it is repeated against the new cart.
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const settle = () => {
        this.dirty = true;
        if (this.isActive()) this.load();
      };
      if (event.promise) event.promise.then(settle, settle);
      else Promise.resolve().then(settle);
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isActive()) this.load();
    }, { signal });

    if (drawer) {
      drawer.addEventListener('theme-drawer:open', () => this.load(), { signal });
      if (drawer.hasAttribute('open')) this.load();
    } else {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '100px' });
      this.observer.observe(this);
    }
  }

  hasContent() {
    return Boolean(this.querySelector('.atw-cart-recommendations__title'));
  }

  // Up to 3 attempts (a section answer can be empty for a moment while the cart is changing).
  async run() {
    this.pending = true;
    let aborted = false;
    try {
      let staleRuns = 0;
      for (let attempt = 0; attempt < 3 && this.isConnected;) {
        this.dirty = false;
        const content = await this.fetchContent();
        if (!this.isConnected) break;
        if (this.dirty && staleRuns < 4) { staleRuns++; continue; }
        if (content) { this.replaceChildren(...content.childNodes); break; }
        attempt++;
        if (attempt < 3) await this.pause(700 * attempt);
      }
    } catch (error) {
      aborted = error.name === 'AbortError';
    } finally {
      this.pending = false;
    }
    // The element was detached and re-attached (morph) while fetching: the new connection must still load.
    if (aborted && this.isConnected && this.isActive()) this.load();
  }

  async fetchContent() {
    const { signal } = this.controller;
    try {
      const read = async (url) => {
        const response = await fetch(url, { signal, cache: 'no-store' });
        if (!response.ok) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return doc.querySelector('[data-atw-cart-recommendations-content]');
      };
      let content = await read(this.dataset.url);
      if (!content?.querySelector('.atw-cart-recommendations__title') && this.dataset.url.includes('intent=complementary')) {
        content = await read(this.dataset.url.replace('intent=complementary', 'intent=related'));
      }
      return content?.querySelector('.atw-cart-recommendations__title') ? content : null;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return null;
    }
  }

  pause(ms) {
    const { signal } = this.controller;
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
    });
  }

  // Desktop: drag the rail with the mouse (touch and trackpads already scroll it natively).
  // Delegated on the element itself, so it keeps working after the rail is re-rendered.
  enableMouseDrag() {
    const opts = {signal: this.controller.signal};
    let rail = null, startX = 0, startLeft = 0, moved = 0;
    this.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      rail = event.target.closest('.atw-cart-recommendations__rail');
      if (!rail || rail.scrollWidth <= rail.clientWidth) { rail = null; return; }
      startX = event.clientX; startLeft = rail.scrollLeft; moved = 0;
    }, opts);
    window.addEventListener('pointermove', event => {
      if (!rail) return;
      const dx = event.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      if (moved > 5) {
        rail.classList.add('is-dragging');
        rail.scrollLeft = startLeft - dx;
      }
    }, opts);
    const end = () => {
      if (!rail) return;
      const current = rail;
      rail = null;
      // Let the snap points settle on release.
      requestAnimationFrame(() => current.classList.remove('is-dragging'));
    };
    window.addEventListener('pointerup', end, opts);
    window.addEventListener('pointercancel', end, opts);
    // A drag must not open the product it ended on.
    this.addEventListener('click', event => {
      if (moved > 5) { event.preventDefault(); event.stopPropagation(); moved = 0; }
    }, {...opts, capture: true});
    this.addEventListener('dragstart', event => {
      if (event.target.closest?.('.atw-cart-recommendations__rail')) event.preventDefault();
    }, opts);
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.refreshObserver?.disconnect(); }
}
if (!customElements.get('atw-cart-recommendations')) customElements.define('atw-cart-recommendations', ATWCartRecommendations);
